import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { TenantProvider } from "@/hooks/useTenant";
import { ProtectedFeature } from "@/components/auth/ProtectedFeature";
import { LanguageProvider } from "@/hooks/useLanguage";
import Landing from "./pages/Landing";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import AuthConfirm from "./pages/AuthConfirm";
import AuthResetPassword from "./pages/AuthResetPassword";
import Onboarding from "./pages/Onboarding";
import Settings from "./pages/Settings";
import DocsCover from "./pages/docs/DocsCover";
import DocsExecutiveSummary from "./pages/docs/DocsExecutiveSummary";
import DocsCoreFeatures from "./pages/docs/DocsCoreFeatures";
import DocsTechnicalSpecs from "./pages/docs/DocsTechnicalSpecs";
import DocsAIIntegration from "./pages/docs/DocsAIIntegration";
import DocsCompetitiveAnalysis from "./pages/docs/DocsCompetitiveAnalysis";
import DocsRolesUserJourneys from "./pages/docs/DocsRolesUserJourneys";
import DocsMarketPosition from "./pages/docs/DocsMarketPosition";
import PitchDeck from "./pages/PitchDeck";
import FeaturesShowcase from "./pages/FeaturesShowcase";
import ProductDemo from "./pages/ProductDemo";
import AdminAnalytics from "./pages/AdminAnalytics";
import Integrations from "./pages/Integrations";
import MicrosoftConsentCallback from "./pages/MicrosoftConsentCallback";
import GoogleConsentCallback from "./pages/GoogleConsentCallback";
import SignalEnginePage from "./pages/SignalEngine";
import ArchitectureDiagrams from "./pages/ArchitectureDiagrams";
import NotFound from "./pages/NotFound";
import SuperadminLayout from "./components/superadmin/SuperadminLayout";
import SuperadminDashboard from "./pages/superadmin/SuperadminDashboard";
import SuperadminTenants from "./pages/superadmin/SuperadminTenants";
import SuperadminUsers from "./pages/superadmin/SuperadminUsers";
import SuperadminBilling from "./pages/superadmin/SuperadminBilling";
import SuperadminProvisioning from "./pages/superadmin/SuperadminProvisioning";
import SuperadminMonitoring from "./pages/superadmin/SuperadminMonitoring";
import SuperadminLLM from "./pages/superadmin/SuperadminLLM";
import MFASetup from "./pages/MFASetup";
import { useSuperadminCheck } from "./hooks/useSuperadmin";
import { Loader2 } from "lucide-react";
import { ErrorBoundary } from "@/components/ErrorBoundary";

const queryClient = new QueryClient();

// Requires authentication only (no tenant needed — used for /onboarding).
const AuthRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" replace />;
  return <>{children}</>;
};

// Requires authentication + a tenant. Wraps children in TenantProvider
// so useTenant() is available to the entire /:slug subtree.
// Falls back to a DB membership check when the JWT has no tenant claim
// (i.e. the auth hook hasn't been registered in Supabase Dashboard yet).
const TenantLayout = () => {
  const { user, loading, tenantSlug } = useAuth();
  const { slug: slugFromUrl } = useParams<{ slug: string }>();
  const queryClient = useQueryClient();
  // null = not a member of slugFromUrl; string = their fallback primary tenant; undefined = query in flight
  const [dbSlug, setDbSlug] = useState<string | null | undefined>(undefined);
  // undefined = not checked yet; null = no slug to check; otherwise the tenant's status
  const [tenantStatus, setTenantStatus] = useState<string | null | undefined>(undefined);
  // True while switching the user's active tenant + re-minting the JWT so it
  // matches the URL slug. Keeps the loader up so nothing ever renders against
  // the stale (pre-switch) tenant's data mid-transition.
  const [switchingTenant, setSwitchingTenant] = useState(false);

  useEffect(() => {
    if (!user) { setDbSlug(null); return; }
    // JWT already agrees with the URL — nothing to check or switch.
    if (tenantSlug && tenantSlug === slugFromUrl) { setSwitchingTenant(false); setDbSlug(null); return; }

    // Either the JWT has no tenant claim yet (hook not registered / very
    // first login), or the JWT's tenant disagrees with the URL slug (the
    // user belongs to more than one tenant and navigated to a different
    // one). Either way: check membership in slugFromUrl first.
    const checkMembership = async () => {
      if (slugFromUrl) {
        const { data: member, error: memberError } = await supabase
          .from('tenant_members')
          .select('tenant_id, tenants!inner(slug)')
          .eq('user_id', user.id)
          .eq('tenants.slug', slugFromUrl)
          .maybeSingle();
        if (memberError) {
          console.error('[TenantSwitch] membership check for', slugFromUrl, 'failed:', memberError);
        }
        if (member) {
          if (tenantSlug) {
            // Member of the URL's tenant, but the JWT is pointed at a
            // different one — switch the active tenant server-side and
            // re-mint the JWT so auth_tenant_id() (and every RLS policy
            // keyed off it) reflects the tenant actually being viewed.
            // Stay on the loader until that new JWT lands (this effect
            // re-runs once tenantSlug updates and hits the branch above).
            setSwitchingTenant(true);
            const { error: switchError } = await supabase.rpc('set_active_tenant', {
              p_tenant_id: member.tenant_id,
            });
            if (switchError) {
              console.error('[TenantSwitch] set_active_tenant failed:', switchError);
              setSwitchingTenant(false);
              setDbSlug(null); // fail safe: stay put rather than hang on the loader forever
              return;
            }
            const { error: refreshError } = await supabase.auth.refreshSession();
            if (refreshError) {
              console.error('[TenantSwitch] refreshSession after switch failed:', refreshError);
              setSwitchingTenant(false);
              setDbSlug(null);
              return;
            }
            // The JWT's tenant claim just changed — every cached query (synced
            // projects, KPIs, tasks, etc.) was fetched under the old tenant's
            // RLS scope and must not be shown against the new one. Clear
            // rather than invalidate so nothing renders a stale flash of the
            // previous tenant's data while switchingTenant's full-page loader
            // is still up.
            queryClient.clear();
            console.info('[TenantSwitch] switched active tenant to', slugFromUrl, '— awaiting refreshed JWT');
            return;
          }
          setDbSlug(null); // stay — no JWT claim at all yet, nothing to switch
          return;
        }
      }
      // Not a member of slugFromUrl (or no slug in URL): redirect to their first tenant
      console.info('[TenantSwitch] not a member of', slugFromUrl, '— falling back to primary tenant');
      const { data } = await supabase
        .from('tenant_members')
        .select('tenants(slug)')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true })
        .limit(1)
        .single();
      setDbSlug((data?.tenants as { slug?: string } | null)?.slug ?? null);
    };

    checkMembership();
  }, [user, tenantSlug, slugFromUrl, queryClient]);

  // If JWT has the slug, use it. If DB check confirmed membership, use URL slug.
  // If DB returned a different slug, that's the redirect target.
  const effectiveSlug = tenantSlug ?? (dbSlug === null ? slugFromUrl : dbSlug);

  useEffect(() => {
    if (!effectiveSlug) { setTenantStatus(null); return; }
    setTenantStatus(undefined);
    supabase
      .from('tenants')
      .select('status')
      .eq('slug', effectiveSlug)
      .maybeSingle()
      .then(({ data }) => setTenantStatus(data?.status ?? null));
  }, [effectiveSlug]);

  if (loading || switchingTenant || dbSlug === undefined || (effectiveSlug && tenantStatus === undefined)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" replace />;
  if (!effectiveSlug) return <Navigate to="/onboarding" replace />;
  // Redirect only if the user is not a member of the URL slug (dbSlug is a different tenant)
  if (slugFromUrl && dbSlug && slugFromUrl !== dbSlug) {
    return <Navigate to={`/${dbSlug}/app`} replace />;
  }

  if (tenantStatus === 'suspended') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="max-w-md text-center space-y-3">
          <h1 className="text-2xl font-bold text-foreground">Organisation Suspended</h1>
          <p className="text-muted-foreground">
            This organisation's access has been suspended. Contact your administrator or Masira support for assistance.
          </p>
        </div>
      </div>
    );
  }

  return (
    <TenantProvider>
      <Outlet />
    </TenantProvider>
  );
};

const SuperadminGuard = () => {
  const { user, loading } = useAuth();
  const isSuperadmin = useSuperadminCheck();

  if (loading || isSuperadmin === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  if (!isSuperadmin) return <Navigate to="/auth" replace />;
  return <SuperadminLayout />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <LanguageProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <Routes>
              {/* Public */}
              <Route path="/" element={<Landing />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/auth/confirm" element={<AuthConfirm />} />
              <Route path="/auth/reset-password" element={<AuthResetPassword />} />

              {/* Auth required, no tenant yet */}
              <Route
                path="/onboarding"
                element={
                  <AuthRoute>
                    <Onboarding />
                  </AuthRoute>
                }
              />
              <Route
                path="/mfa-setup"
                element={
                  <AuthRoute>
                    <MFASetup />
                  </AuthRoute>
                }
              />
              <Route
                path="/integrations/ms-callback"
                element={
                  <AuthRoute>
                    <MicrosoftConsentCallback />
                  </AuthRoute>
                }
              />
              <Route
                path="/integrations/google-callback"
                element={
                  <AuthRoute>
                    <GoogleConsentCallback />
                  </AuthRoute>
                }
              />

              {/* Tenant-scoped app routes — all under /:slug/ */}
              <Route path="/:slug" element={<TenantLayout />}>
                <Route index element={<Navigate to="app" replace />} />
                <Route
                  path="app"
                  element={
                    <ErrorBoundary>
                      <Index />
                    </ErrorBoundary>
                  }
                />
                <Route path="settings" element={<Settings />} />
                <Route
                  path="integrations"
                  element={
                    <ProtectedFeature feature="integrations">
                      <Integrations />
                    </ProtectedFeature>
                  }
                />
                <Route
                  path="signals"
                  element={
                    <ErrorBoundary>
                      <SignalEnginePage />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="admin/analytics"
                  element={
                    <ErrorBoundary>
                      <AdminAnalytics />
                    </ErrorBoundary>
                  }
                />
              </Route>

              {/* Legacy redirects — keep old /app working */}
              <Route path="/app" element={<Navigate to="/auth" replace />} />
              <Route path="/settings" element={<Navigate to="/auth" replace />} />
              <Route path="/integrations" element={<Navigate to="/auth" replace />} />
              <Route path="/admin/analytics" element={<Navigate to="/auth" replace />} />

              {/* Public marketing / docs */}
              <Route path="/docs" element={<DocsCover />} />
              <Route path="/docs/executive-summary" element={<DocsExecutiveSummary />} />
              <Route path="/docs/core-features" element={<DocsCoreFeatures />} />
              <Route path="/docs/technical-specs" element={<DocsTechnicalSpecs />} />
              <Route path="/docs/ai-integration" element={<DocsAIIntegration />} />
              <Route path="/docs/competitive-analysis" element={<DocsCompetitiveAnalysis />} />
              <Route path="/docs/roles-user-journeys" element={<DocsRolesUserJourneys />} />
              <Route path="/docs/market-position" element={<DocsMarketPosition />} />
              <Route path="/pitch-deck" element={<PitchDeck />} />
              <Route path="/features" element={<FeaturesShowcase />} />
              <Route path="/demo" element={<ProductDemo />} />
              <Route path="/architecture" element={<ArchitectureDiagrams />} />

              {/* Superadmin portal — platform-owner only */}
              <Route path="/superadmin" element={<SuperadminGuard />}>
                <Route index element={<SuperadminDashboard />} />
                <Route path="tenants" element={<SuperadminTenants />} />
                <Route path="users" element={<SuperadminUsers />} />
                <Route path="billing" element={<SuperadminBilling />} />
                <Route path="provisioning" element={<SuperadminProvisioning />} />
                <Route path="monitoring" element={<SuperadminMonitoring />} />
                <Route path="llm" element={<SuperadminLLM />} />
              </Route>

              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </LanguageProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
